import { useMemo, useState } from 'react'
import { Loader2, ListPlus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  useBackfillConnectionHealthChecksMutation,
  useGetConnectionHealthRollupQuery,
  useGetConnectionHealthStatusQuery,
  type BackfillConnectionHealthChecksResult,
  type ConnectionHealthGatewayRollup
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { StatusPill } from '../../../components/primitives/StatusPill'
import {
  checkKindLabel,
  formatAge,
  formatBackfillSummary,
  formatLatency,
  gatewayLabel,
  groupRowsByGateway,
  statusLabel,
  statusToPill
} from './health-view'

const POLL_INTERVAL_MS = 15000

/** Compact "3 up · 1 degraded" summary from a gateway's rollup counts. */
function RollupCounts({ rollup }: { rollup?: ConnectionHealthGatewayRollup }) {
  if (!rollup) return null
  const parts: Array<{ key: string; label: string; className: string }> = []
  if (rollup.up > 0) parts.push({ key: 'up', label: `${rollup.up} up`, className: 'text-st-run' })
  if (rollup.degraded > 0)
    parts.push({ key: 'degraded', label: `${rollup.degraded} degraded`, className: 'text-st-warn' })
  if (rollup.down > 0)
    parts.push({ key: 'down', label: `${rollup.down} down`, className: 'text-st-down' })
  if (rollup.unknown > 0)
    parts.push({ key: 'unknown', label: `${rollup.unknown} unknown`, className: 'text-ink-3' })
  if (parts.length === 0) return null
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-11 font-medium">
      {parts.map((p) => (
        <span key={p.key} className={p.className}>
          {p.label}
        </span>
      ))}
    </span>
  )
}

/**
 * Live connection-health surface (Design §9): every check's latest reading,
 * grouped by gateway and laid out in data-path chain order
 * (gateway → OPC → device → DB → remote gateway) so a failure's location is
 * obvious. Shows status, latency, staleness, and the last error. Polls so the
 * view stays live between runner ticks.
 */
export function ConnectionHealthStatusCard() {
  const [search, setSearch] = useState('')
  const [lastBackfill, setLastBackfill] = useState<BackfillConnectionHealthChecksResult | null>(
    null
  )
  const name = search.trim()

  const statusQuery = useGetConnectionHealthStatusQuery(name ? { name } : undefined, {
    pollingInterval: POLL_INTERVAL_MS
  })
  const rollupQuery = useGetConnectionHealthRollupQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS
  })
  const [backfillChecks, { isLoading: isBackfilling }] = useBackfillConnectionHealthChecksMutation()

  const statuses = statusQuery.data?.statuses
  const groups = useMemo(() => groupRowsByGateway(statuses ?? []), [statuses])

  const rollupByGateway = useMemo(() => {
    const map = new Map<string, ConnectionHealthGatewayRollup>()
    for (const g of rollupQuery.data?.gateways ?? []) {
      map.set(g.gateway_url, g)
    }
    return map
  }, [rollupQuery.data])

  const isLoading = statusQuery.isLoading || rollupQuery.isLoading
  const error = statusQuery.error || rollupQuery.error
  const isRefreshing = statusQuery.isFetching || rollupQuery.isFetching

  const onRefresh = () => {
    void statusQuery.refetch()
    void rollupQuery.refetch()
  }

  const onProvision = async () => {
    try {
      const result = await backfillChecks().unwrap()
      setLastBackfill(result)
      if (result.failed > 0 || result.gateways === 0) {
        toast.message(formatBackfillSummary(result))
      } else {
        toast.success(formatBackfillSummary(result))
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Connection health</h3>
            <p className="text-xs text-ink-3">
              Live status of each gateway&apos;s data-access chain: gateway, OPC, devices, and
              databases. Ordered along the data path so a failure&apos;s location is clear.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              size="xs"
              variant="outline"
              onClick={() => void onProvision()}
              disabled={isBackfilling}
              aria-label="Provision connection-health checks"
            >
              {isBackfilling ? <Loader2 className="animate-spin" /> : <ListPlus />}
              Provision checks
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh connection health"
            >
              <RefreshCw className={isRefreshing ? 'animate-spin' : undefined} /> Refresh
            </Button>
          </div>
        </div>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by target or label…"
          aria-label="Filter connection-health checks"
          className="h-8 text-xs"
        />

        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            Failed to load connection health.
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
            <Loader2 className="size-3 animate-spin" />
            Loading connection health…
          </div>
        ) : null}

        {!isLoading && !error && groups.length === 0 ? (
          <p className="py-2.5 text-xs text-ink-3">
            {name
              ? 'No checks match your filter.'
              : 'No connection-health checks yet. They are auto-provisioned when you ingest a gateway backup, or click Provision checks to backfill from the live graph.'}
          </p>
        ) : null}

        {lastBackfill ? (
          <div className="rounded-md border border-line px-3 py-2 text-xs text-ink-2">
            <p>{formatBackfillSummary(lastBackfill)}</p>
            {lastBackfill.errors.length > 0 ? (
              <ul className="mt-1 list-disc pl-4 text-st-down">
                {lastBackfill.errors.slice(0, 5).map((err, index) => (
                  <li key={`${err.gatewayUrl}:${err.checkKind ?? ''}:${err.target ?? ''}:${index}`}>
                    {err.gatewayUrl}
                    {err.checkKind ? ` · ${err.checkKind}` : ''}
                    {err.target ? ` · ${err.target}` : ''}: {err.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.gatewayUrl} className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1">
              <span className="truncate text-xs font-semibold text-ink" title={group.gatewayUrl}>
                {gatewayLabel(group.gatewayUrl)}
              </span>
              <RollupCounts rollup={rollupByGateway.get(group.gatewayUrl)} />
            </div>
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-ink-3">
                  <th className="py-1 pr-2 font-medium">Check</th>
                  <th className="py-1 pr-2 font-medium">Target</th>
                  <th className="py-1 pr-2 font-medium">Status</th>
                  <th className="py-1 pr-2 font-medium">Latency</th>
                  <th className="py-1 pr-2 font-medium">Last update</th>
                  <th className="py-1 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr
                    key={`${row.check_kind}:${row.target}`}
                    className="border-t border-line/60 align-top"
                  >
                    <td className="py-1 pr-2 text-ink-2">{checkKindLabel(row.check_kind)}</td>
                    <td className="py-1 pr-2 text-ink-2">
                      <span className="break-all">{row.target}</span>
                    </td>
                    <td className="py-1 pr-2">
                      <StatusPill status={statusToPill(row.status)}>
                        {statusLabel(row.status)}
                      </StatusPill>
                    </td>
                    <td className="py-1 pr-2 tabular-nums text-ink-2">
                      {formatLatency(row.latency_ms)}
                    </td>
                    <td className="py-1 pr-2 tabular-nums text-ink-3">{formatAge(row.age_ms)}</td>
                    <td className="py-1 text-ink-3">
                      {row.error ? (
                        <span className="break-words text-st-down">{row.error}</span>
                      ) : row.value ? (
                        <span className="break-words">{row.value}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
